import { useCallback, useEffect, useState } from 'react'

import { openFile } from '../../lib/api'
import { errorText } from '../../lib/offsite'
import { useSession } from '../../lib/SessionContext'
import { canEdit } from '../../lib/nav'

/* ============================================================================
   ข่าว/ประกาศ/ฟอร์ม — จัดการโดย admin · `/admin/news`
   API: backend/src/routes/adminNews.js · lib/news.js · ตาราง company_news (016)

   ย้ายมาจาก Google Apps Script ของ hr.simat.co.th/HomeSite3.aspx เพื่อให้ admin
   แก้เองได้ สิ่งที่บันทึกที่นี่จะไปโผล่หน้าแรกฝั่งพนักงาน (Home.tsx)
   ============================================================================ */

type News = {
  id: number
  category: string
  categoryTh: string
  title: string
  linkUrl: string | null
  expireDate: string | null
  pinned: boolean
  isActive: boolean
  sortOrder: number
  hasFile: boolean
  fileName: string | null
  fileSize: number | null
}

type Draft = {
  category: string
  title: string
  expireDate: string
  pinned: boolean
  isActive: boolean
  sortOrder: number
  // ไฟล์แนบ: fileData = base64 data URL ของไฟล์ใหม่ · removeFile = ลบไฟล์เดิม
  fileData: string | null
  fileName: string | null
  removeFile: boolean
}

const CATEGORIES: { code: string; th: string }[] = [
  { code: 'news', th: 'ข่าวประชาสัมพันธ์' },
  { code: 'announce', th: 'ประกาศ' },
  { code: 'policy', th: 'นโยบาย' },
  { code: 'rule', th: 'กฎระเบียบ' },
  { code: 'form', th: 'แบบฟอร์ม' },
  { code: 'knowledge', th: 'คลังความรู้' },
]

const emptyDraft = (): Draft => ({
  category: 'announce',
  title: '',
  expireDate: '',
  pinned: false,
  isActive: true,
  sortOrder: 0,
  fileData: null,
  fileName: null,
  removeFile: false,
})

const MAX_FILE_MB = 20

/** อ่านไฟล์เป็น base64 data URL (แบบเดียวกับที่ backend คาด) */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'))
    r.readAsDataURL(file)
  })
}

export default function News() {
  const { state, call } = useSession()
  const editable = state.status === 'signed-in' && canEdit(state.session)

  const [items, setItems] = useState<News[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [q, setQ] = useState('')

  // แผงแก้ไข: null = ปิด · {id:null} = เพิ่มใหม่ · {id:n} = แก้
  const [editing, setEditing] = useState<{ id: number | null } | null>(null)
  const [draft, setDraft] = useState<Draft>(emptyDraft())
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (filter) qs.set('category', filter)
      if (q.trim()) qs.set('q', q.trim())
      const d = await call<{ items: News[] }>(`/api/admin/news?${qs}`)
      setItems(d.items)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [call, filter, q])

  useEffect(() => {
    void load()
  }, [load])

  function openAdd() {
    setDraft(emptyDraft())
    setEditing({ id: null })
  }
  function openEdit(r: News) {
    setDraft({
      category: r.category,
      title: r.title,
      expireDate: r.expireDate ?? '',
      pinned: r.pinned,
      isActive: r.isActive,
      sortOrder: r.sortOrder,
      fileData: null,
      fileName: r.fileName,
      removeFile: false,
    })
    setEditing({ id: r.id })
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`ไฟล์ใหญ่เกิน ${MAX_FILE_MB} MB`)
      e.target.value = ''
      return
    }
    try {
      const dataUrl = await readFileAsDataUrl(file)
      setDraft((d) => ({ ...d, fileData: dataUrl, fileName: file.name, removeFile: false }))
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const body = { ...draft }
      if (editing?.id == null) {
        await call('/api/admin/news', { method: 'POST', body: JSON.stringify(body) })
      } else {
        await call(`/api/admin/news/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) })
      }
      setEditing(null)
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setSaving(false)
    }
  }

  async function remove(r: News) {
    if (!confirm(`ลบ "${r.title}" ?`)) return
    setError(null)
    try {
      await call(`/api/admin/news/${r.id}`, { method: 'DELETE' })
      await load()
    } catch (err) {
      setError(errorText(err))
    }
  }

  return (
    <>
      <h1>ข่าว / ประกาศ / ฟอร์ม</h1>

      <section className="card">
        <div className="section-head">
          <div>
            <p className="meta">สิ่งที่เพิ่มที่นี่จะแสดงหน้าแรกของพนักงาน · ปิดไว้ = ยังไม่แสดง</p>
            <div className="emp-toolbar">
              <label className="kv">
                <span className="kv-label">หมวด</span>
                <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  {CATEGORIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.th}
                    </option>
                  ))}
                </select>
              </label>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ค้นหัวข้อ"
                aria-label="ค้นหัวข้อ"
              />
            </div>
          </div>
          {editable && (
            <button type="button" onClick={openAdd} disabled={loading}>
              เพิ่มรายการ
            </button>
          )}
        </div>
        {error && <p className="error">{error}</p>}

        {loading ? (
          <p className="meta">กำลังโหลด…</p>
        ) : items.length === 0 ? (
          <p className="meta">ไม่มีรายการ</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>หมวด</th>
                  <th>หัวข้อ</th>
                  <th>หมดอายุ</th>
                  <th className="num">ลำดับ</th>
                  <th>สถานะ</th>
                  {editable && <th></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className={r.isActive ? undefined : 'meta'}>
                    <td className="nowrap">{r.categoryTh}</td>
                    <td>
                      {r.pinned && <span className="chip chip--warn">ปักหมุด</span>}
                      {r.hasFile ? (
                        /* มีไฟล์ใน DB → โหลดผ่าน fetch แล้วเปิดเป็น blob (ข้าม origin ชัวร์กว่า) */
                        <button
                          type="button"
                          className="linklike"
                          onClick={() =>
                            void openFile(`/api/admin/news/${r.id}/file`).catch((e) =>
                              setError(errorText(e)),
                            )
                          }
                        >
                          {r.title}
                        </button>
                      ) : (
                        r.title
                      )}
                      {r.hasFile ? (
                        <span className="chip">ไฟล์แนบ</span>
                      ) : (
                        /* ไม่มีไฟล์ = ไฟล์เก่าตาย รอ HR อัปใหม่ */
                        <span className="chip chip--warn">ยังไม่มีไฟล์</span>
                      )}
                    </td>
                    <td className="nowrap">{r.expireDate ?? '—'}</td>
                    <td className="num">{r.sortOrder}</td>
                    <td className="nowrap">
                      {r.isActive ? (
                        <span className="chip chip--ok">แสดง</span>
                      ) : (
                        <span className="chip chip--off">ปิด</span>
                      )}
                    </td>
                    {editable && (
                      <td className="row-actions nowrap">
                        <button type="button" className="ghost" onClick={() => openEdit(r)}>
                          แก้
                        </button>
                        <button type="button" className="ghost danger" onClick={() => void remove(r)}>
                          ลบ
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <section className="card form-card">
          <h2>{editing.id == null ? 'เพิ่มรายการ' : 'แก้ไขรายการ'}</h2>
          <div className="field-grid">
            <label className="kv">
              <span className="kv-label">หมวด</span>
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.th}
                  </option>
                ))}
              </select>
            </label>

            <label className="kv field-grid--full">
              <span className="kv-label">หัวข้อ</span>
              <input
                type="text"
                value={draft.title}
                maxLength={500}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>

            {/* ไม่มีช่องลิงก์ภายนอกแล้ว — เอกสารทุกใบเก็บเป็นไฟล์ในฐานข้อมูล */}
            <div className="kv field-grid--full">
              <span className="kv-label">ไฟล์แนบ (PDF/รูป/เอกสาร ไม่เกิน {MAX_FILE_MB}MB)</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx"
                onChange={onPickFile}
              />
              {draft.fileData ? (
                <span className="meta">จะอัป: {draft.fileName}</span>
              ) : draft.fileName && !draft.removeFile ? (
                <span className="meta">
                  ไฟล์เดิม: {draft.fileName}{' '}
                  <button
                    type="button"
                    className="ghost danger"
                    onClick={() => setDraft({ ...draft, removeFile: true, fileName: null })}
                  >
                    ลบไฟล์
                  </button>
                </span>
              ) : draft.removeFile ? (
                <span className="meta">จะลบไฟล์เดิมเมื่อบันทึก</span>
              ) : null}
            </div>

            <label className="kv">
              <span className="kv-label">วันหมดอายุ (ว่าง = ไม่หมด)</span>
              <input
                type="date"
                value={draft.expireDate}
                onChange={(e) => setDraft({ ...draft, expireDate: e.target.value })}
              />
            </label>

            <label className="kv">
              <span className="kv-label">ลำดับ (น้อย = บน)</span>
              <input
                type="number"
                value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) || 0 })}
              />
            </label>

            <label className="kv">
              <span className="kv-label">ปักหมุด</span>
              <input
                type="checkbox"
                checked={draft.pinned}
                onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
              />
            </label>

            <label className="kv">
              <span className="kv-label">แสดงหน้าแรก</span>
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
            </label>
          </div>

          <div className="row-actions">
            <button type="button" onClick={save} disabled={saving || !draft.title.trim()}>
              {saving ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
            <button type="button" className="ghost" onClick={() => setEditing(null)} disabled={saving}>
              ยกเลิก
            </button>
          </div>
        </section>
      )}
    </>
  )
}
